// SoundManager - Centralized audio management for Buzzer Bash
// Uses Web Audio API for better control and mobile support

class SoundManager {
  constructor() {
    this.audioContext = null;
    this.sounds = {};
    this.musicGainNode = null;
    this.sfxGainNode = null;
    this.musicEnabled = true;
    this.sfxEnabled = true;
    this.isUnlocked = false;
    this.currentMusic = null;
    this.musicVolume = 0.3;
    this.sfxVolume = 0.5;
  }

  // Initialize audio context (must be called after user interaction)
  async init() {
    if (this.audioContext) return true;
    
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext();
      
      // Create gain nodes for volume control
      this.musicGainNode = this.audioContext.createGain();
      this.musicGainNode.gain.value = this.musicVolume;
      this.musicGainNode.connect(this.audioContext.destination);
      
      this.sfxGainNode = this.audioContext.createGain();
      this.sfxGainNode.gain.value = this.sfxVolume;
      this.sfxGainNode.connect(this.audioContext.destination);
      
      // Resume context if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      this.isUnlocked = true;
      console.log('🔊 SoundManager initialized');
      return true;
    } catch (error) {
      console.error('Failed to initialize SoundManager:', error);
      return false;
    }
  }

  // Play a synthesized tone (for instant feedback)
  playTone(frequency, duration = 0.15, type = 'sine', volume = 0.3) {
    if (!this.audioContext || !this.sfxEnabled) return;
    
    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      
      gainNode.gain.setValueAtTime(volume * this.sfxVolume, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      oscillator.start();
      oscillator.stop(this.audioContext.currentTime + duration);
    } catch (error) {
      console.warn('Tone playback failed:', error);
    }
  }

  // Play multiple tones in sequence (for melodies)
  playMelody(notes, baseDelay = 100) {
    if (!this.audioContext || !this.sfxEnabled) return;
    
    notes.forEach(([freq, duration, delay, type, volume], index) => {
      setTimeout(() => {
        this.playTone(freq, duration / 1000, type || 'sine', volume || 0.3);
      }, delay || (index * baseDelay));
    });
  }

  // Sound effect presets
  playCorrect() {
    // Triumphant ascending arpeggio
    this.playMelody([
      [523.25, 100, 0, 'sine', 0.4],      // C5
      [659.25, 100, 80, 'sine', 0.35],    // E5
      [783.99, 150, 160, 'sine', 0.3],    // G5
      [1046.50, 200, 260, 'sine', 0.25],  // C6
    ]);
  }

  playWrong() {
    // Descending "wah wah" 
    this.playMelody([
      [311.13, 150, 0, 'sawtooth', 0.25],   // Eb4
      [277.18, 200, 150, 'sawtooth', 0.2],  // Db4
    ]);
  }

  playBuzz() {
    // Quick double beep for answer submission
    this.playMelody([
      [440, 80, 0, 'square', 0.3],     // A4
      [587.33, 100, 90, 'square', 0.25], // D5
    ]);
  }

  playCountdown(secondsLeft) {
    // Increasingly urgent countdown ticks
    const frequency = 600 + (5 - Math.min(secondsLeft, 5)) * 100;
    this.playTone(frequency, 0.08, 'sine', 0.35);
  }

  playJoin() {
    // Welcoming ascending chime
    this.playMelody([
      [523.25, 100, 0, 'sine', 0.3],     // C5
      [659.25, 100, 100, 'sine', 0.25],  // E5
      [783.99, 150, 200, 'sine', 0.2],   // G5
    ]);
  }

  playGameStart() {
    // Exciting fanfare
    this.playMelody([
      [523.25, 100, 0, 'square', 0.3],      // C5
      [659.25, 100, 120, 'square', 0.28],   // E5
      [783.99, 100, 240, 'square', 0.26],   // G5
      [1046.50, 200, 380, 'sine', 0.3],     // C6
    ]);
  }

  playRoundStart() {
    // Building anticipation
    this.playMelody([
      [392, 150, 0, 'sine', 0.3],      // G4
      [440, 150, 200, 'sine', 0.3],    // A4
      [493.88, 150, 400, 'sine', 0.3], // B4
      [523.25, 300, 600, 'sine', 0.35], // C5
    ]);
  }

  playNewQuestion() {
    // Attention-grabbing notification
    this.playMelody([
      [880, 80, 0, 'sine', 0.25],      // A5
      [1108.73, 120, 100, 'sine', 0.2], // C#6
    ]);
  }

  playVictory() {
    // Grand victory fanfare
    this.playMelody([
      [523.25, 150, 0, 'square', 0.3],      // C5
      [659.25, 150, 180, 'square', 0.28],   // E5
      [783.99, 150, 360, 'square', 0.26],   // G5
      [1046.50, 150, 540, 'sine', 0.3],     // C6
      [987.77, 150, 720, 'sine', 0.28],     // B5
      [1046.50, 400, 900, 'sine', 0.35],    // C6 (held)
    ]);
  }

  playGameOver() {
    // Descending end theme
    this.playMelody([
      [783.99, 200, 0, 'sine', 0.3],     // G5
      [659.25, 200, 250, 'sine', 0.28],  // E5
      [523.25, 300, 500, 'sine', 0.25],  // C5
      [392, 400, 800, 'sine', 0.2],      // G4
    ]);
  }

  playStreak(streakCount) {
    // Escalating streak sound
    const baseFreq = 523.25 + (Math.min(streakCount, 5) * 50);
    this.playMelody([
      [baseFreq, 80, 0, 'sine', 0.3],
      [baseFreq * 1.25, 80, 60, 'sine', 0.28],
      [baseFreq * 1.5, 100, 120, 'sine', 0.25],
    ]);
  }

  playClick() {
    // Subtle UI click
    this.playTone(800, 0.05, 'sine', 0.15);
  }

  playHover() {
    // Very subtle hover sound
    this.playTone(600, 0.03, 'sine', 0.08);
  }

  playTimerWarning() {
    // Urgent timer warning (last 5 seconds)
    this.playMelody([
      [880, 60, 0, 'square', 0.35],
      [880, 60, 200, 'square', 0.35],
    ]);
  }

  playLeaderAnnounce() {
    // Regal leader announcement
    this.playMelody([
      [659.25, 150, 0, 'sine', 0.3],     // E5
      [783.99, 150, 180, 'sine', 0.3],   // G5
      [987.77, 200, 360, 'sine', 0.35],  // B5
    ]);
  }

  playWheelSpin() {
    // Continuous spinning sound (call repeatedly)
    this.playTone(440 + Math.random() * 200, 0.05, 'sine', 0.15);
  }

  playWheelStop() {
    // Satisfying landing sound
    this.playMelody([
      [523.25, 100, 0, 'sine', 0.4],
      [659.25, 150, 100, 'sine', 0.35],
      [783.99, 200, 250, 'sine', 0.3],
    ]);
  }

  // Simple background music using oscillators (generates procedural chill music)
  startBackgroundMusic() {
    if (!this.audioContext || !this.musicEnabled || this.currentMusic) return;
    
    try {
      // Create a simple ambient pad
      const createPad = (freq, detune = 0) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        const filter = this.audioContext.createBiquadFilter();
        
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.detune.value = detune;
        
        filter.type = 'lowpass';
        filter.frequency.value = 800;
        filter.Q.value = 1;
        
        gain.gain.value = 0;
        
        osc.connect(filter);
        filter.connect(gain);
        gain.connect(this.musicGainNode);
        
        osc.start();
        
        return { osc, gain, filter };
      };
      
      // Create chord tones (C major 7 chord, very ambient)
      const pads = [
        createPad(130.81, 0),   // C3
        createPad(164.81, 5),   // E3
        createPad(196.00, -5),  // G3
        createPad(246.94, 3),   // B3
      ];
      
      // Fade in
      pads.forEach(pad => {
        pad.gain.gain.setTargetAtTime(0.08, this.audioContext.currentTime, 2);
      });
      
      // Slowly modulate volume for movement
      const modulateVolume = () => {
        if (!this.currentMusic) return;
        
        const time = this.audioContext.currentTime;
        pads.forEach((pad, i) => {
          const modulation = 0.06 + Math.sin(time * 0.1 + i * 0.5) * 0.02;
          pad.gain.gain.setTargetAtTime(modulation, time, 0.5);
        });
        
        setTimeout(modulateVolume, 500);
      };
      
      this.currentMusic = { pads, modulateVolume };
      modulateVolume();
      
      console.log('🎵 Background music started');
    } catch (error) {
      console.warn('Failed to start background music:', error);
    }
  }

  stopBackgroundMusic() {
    if (!this.currentMusic) return;
    
    try {
      const { pads } = this.currentMusic;
      
      // Fade out
      pads.forEach(pad => {
        pad.gain.gain.setTargetAtTime(0, this.audioContext.currentTime, 1);
        setTimeout(() => {
          pad.osc.stop();
        }, 3000);
      });
      
      this.currentMusic = null;
      console.log('🎵 Background music stopped');
    } catch (error) {
      console.warn('Failed to stop background music:', error);
    }
  }

  toggleMusic() {
    this.musicEnabled = !this.musicEnabled;
    if (this.musicEnabled) {
      this.startBackgroundMusic();
    } else {
      this.stopBackgroundMusic();
    }
    return this.musicEnabled;
  }

  toggleSfx() {
    this.sfxEnabled = !this.sfxEnabled;
    return this.sfxEnabled;
  }

  setMusicVolume(volume) {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    if (this.musicGainNode) {
      this.musicGainNode.gain.setTargetAtTime(this.musicVolume, this.audioContext.currentTime, 0.1);
    }
  }

  setSfxVolume(volume) {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
  }

  // Haptic feedback for mobile devices
  vibrate(pattern = 50) {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }

  vibrateCorrect() {
    this.vibrate([50, 30, 50]);
  }

  vibrateWrong() {
    this.vibrate([100, 50, 100]);
  }

  vibrateBuzz() {
    this.vibrate(30);
  }
}

// Singleton instance
const soundManager = new SoundManager();

export default soundManager;

